import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, ArrowRight, Smartphone, CreditCard, Package,
    ShoppingBag, CheckCircle2,
} from 'lucide-react';
import { notify } from '../../components/Notify';
import AuthShell from '../../components/AuthShell';
import { TextField, PasswordField, Field } from '../../components/Fields';
import {
    verifyCpfExistApi, verifyEmailExistApi, getProdutosApi,
    registerUserApi, createPixPaymentApi, createCardPaymentApi, getPagamentoConfigApi,
    listarCursosFunemgApi,
} from '../../api';
import { setToken, setUser } from '../../auth/storage';
import {
    formatCPF, formatPhone, validateCPF, validatePhone,
    validateEmailCadastro, validateDateOfBirth,
} from '../../validators';
import CardForm from '../../components/CardForm';
import FotoCropUpload from '../../components/FotoCropUpload';
import CursoComboBox from '../../components/CursoComboBox';
import ComprovanteUpload from '../../components/ComprovanteUpload';
import FreteCalculator from '../Produtos/FreteCalculator';
import PixWaiting from '../Produtos/PixWaiting';
import { QrCode, CreditCard as CartaoIco } from 'lucide-react';
import './registro.css';

const VAZIO = {
    nomeCompleto: '', cpf: '', telefone: '',
    email: '', confirmEmail: '', dataNascimento: '', password: '', aceitouTermos: false,
};

// Níveis de ensino do UEB (id numérico gravado em users_carteirinhas.escolaridade;
// o mesmo mapa que o backend usa pra render — escolaridadeMap em utils/estudante).
const ESCOLARIDADES = [
    { id: 1, label: 'Ensino Fundamental' },
    { id: 3, label: 'Ensino Médio' },
    { id: 12, label: 'Técnico' },
    { id: 5, label: 'Ensino Superior' },
    { id: 7, label: 'Pós-graduação' },
    { id: 9, label: 'Mestrado' },
    { id: 11, label: 'Doutorado' },
];

// Atalho "Matricular na FUNEMG": instituição fixa + curso vindo do EAD + nível travado.
const FUNEMG_INSTITUICAO = 'FUNEMG';
const TECNICO_ID = 12; // "Técnico" em users_carteirinhas.escolaridade

const moeda = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

function iconeProduto(p) {
    if (p.fisica === 1 && p.digital === 1) return CreditCard;
    if (p.fisica === 1) return Package;
    return Smartphone;
}

export default function RegistrarUser() {
    const navigate = useNavigate();
    const [passo, setPasso] = useState(1);
    const [form, setForm] = useState(VAZIO);
    const [erros, setErros] = useState({});
    const [verificando, setVerificando] = useState(false);

    // --- Passo 3 (produto + dados da carteirinha + pagamento) ---
    const [produtos, setProdutos] = useState(null);
    const [produtoSel, setProdutoSel] = useState(null);
    const [dados, setDados] = useState({ instituicao: '', curso: '', escolaridade: '', foto: '', comprovante: '' });
    const [matricularFunemg, setMatricularFunemg] = useState(false);
    const [cursosFunemg, setCursosFunemg] = useState([]); // [{id, titulo, nome_carteirinha, descricao, capa_url}]
    const [cursoEadId, setCursoEadId] = useState('');     // id do curso FUNEMG escolhido
    // Frete (física): resultado do FreteCalculator — { cepDestino, freteValor,
    // codigoServico, modalidadeNome, endereco }. Modo fixo (SEDEX/Carta Reg) ou Correios.
    const [frete, setFrete] = useState(null);
    const [fase3, setFase3] = useState('form'); // form | pix | done
    // Sub-etapa da compra (passo 3): guia o aluno uma tela por vez.
    const [etapaCompra, setEtapaCompra] = useState('carteira'); // carteira | dados | endereco | pagamento
    const [pix, setPix] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [metodoPag, setMetodoPag] = useState('pix'); // pix | cartao
    const [cartaoConfig, setCartaoConfig] = useState({ ativo: false, publicKey: '' });

    const set = (campo) => (e) => setForm((p) => ({ ...p, [campo]: e.target.value }));
    const setD = (campo) => (e) => setDados((p) => ({ ...p, [campo]: e.target.value }));

    // Nome que vai na carteirinha (nome_carteirinha tem prioridade sobre o título).
    const nomeCurso = (c) => ((c?.nome_carteirinha || '').trim() || (c?.titulo || '').trim());

    // Switch "Matricular na FUNEMG": liga → instituição FUNEMG fixa, curso da lista
    // do EAD (o primeiro, que é Administração) e nível travado em Técnico; some o
    // comprovante. Desliga → campos livres de novo.
    const toggleFunemg = (on) => {
        setMatricularFunemg(on);
        if (on) {
            const c0 = cursosFunemg[0];
            setCursoEadId(c0 ? String(c0.id) : '');
            setDados((p) => ({ ...p, instituicao: FUNEMG_INSTITUICAO, curso: nomeCurso(c0), escolaridade: TECNICO_ID, comprovante: '' }));
        } else {
            setCursoEadId('');
            setDados((p) => ({ ...p, instituicao: '', curso: '', escolaridade: '' }));
        }
    };

    // Troca o curso FUNEMG escolhido (atualiza o id e o nome que vai na carteirinha).
    const selecionarCursoFunemg = (id) => {
        setCursoEadId(id);
        const c = cursosFunemg.find((x) => String(x.id) === String(id));
        setDados((p) => ({ ...p, curso: nomeCurso(c) }));
    };
    const cursoFunemgSel = cursosFunemg.find((x) => String(x.id) === String(cursoEadId)) || null;

    // Produto físico (combo digital+física) exige endereço + frete no cadastro.
    const precisaEndereco = produtoSel?.fisica === 1;
    // Total a cobrar = produto + frete (só quando física, e o frete foi escolhido).
    const totalCompra = (Number(produtoSel?.price) || 0) + (precisaEndereco ? (Number(frete?.freteValor) || 0) : 0);

    // Carrega os produtos (Produtos por ano, já cadastrados) ao entrar no passo 3.
    // Cursos da FUNEMG (catálogo público) — pro switch "Matricular na FUNEMG".
    useEffect(() => {
        let vivo = true;
        listarCursosFunemgApi()
            .then((res) => { if (vivo) setCursosFunemg(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { if (vivo) setCursosFunemg([]); });
        return () => { vivo = false; };
    }, []);

    // Se o switch está ligado e os cursos chegaram depois, seleciona o 1º (Administração).
    useEffect(() => {
        if (!matricularFunemg || !cursosFunemg.length || cursoEadId) return;
        const c0 = cursosFunemg[0];
        setCursoEadId(String(c0.id));
        setDados((p) => ({ ...p, curso: nomeCurso(c0) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matricularFunemg, cursosFunemg, cursoEadId]);

    useEffect(() => {
        if (passo !== 3 || produtos !== null) return;
        let vivo = true;
        (async () => {
            try {
                const [res, cfgRes] = await Promise.all([
                    getProdutosApi(),
                    getPagamentoConfigApi().catch(() => null),
                ]);
                const lista = Array.isArray(res?.data) ? res.data : (res?.data?.produtos || []);
                // O portal só vende digital por ora (sem Correios/frete).
                if (!vivo) return;
                setProdutos(lista.filter((p) => p.digital === 1));
                setCartaoConfig({ ativo: !!cfgRes?.data?.cartao_ativo, publicKey: cfgRes?.data?.public_key || '' });
            } catch {
                if (vivo) setProdutos([]);
            }
        })();
        return () => { vivo = false; };
    }, [passo, produtos]);

    // ---- Passo 1: dados pessoais (+ checa CPF já cadastrado) ----
    const validarPasso1 = () => {
        const e = {};
        if (!form.nomeCompleto.trim() || !form.nomeCompleto.trim().includes(' ')) e.nomeCompleto = 'Informe o nome completo.';
        const vc = validateCPF(form.cpf); if (!vc.valid) e.cpf = vc.error;
        const vt = validatePhone(form.telefone); if (!vt.valid) e.telefone = vt.error;
        setErros(e);
        return Object.keys(e).length === 0;
    };

    const irPasso2 = async () => {
        if (!validarPasso1() || verificando) return;
        setVerificando(true);
        try {
            const res = await verifyCpfExistApi({ cpf: form.cpf });
            if (res?.data?.find === true) {
                notify('Este CPF já tem conta. Faça login ou recupere o acesso.');
                navigate('/login');
                return;
            }
            setPasso(2);
        } catch {
            // Backend ainda sem a rota: segue o fluxo pra não travar o front.
            setPasso(2);
        } finally {
            setVerificando(false);
        }
    };

    // ---- Passo 2: acesso (+ checa email já cadastrado) ----
    const validarPasso2 = () => {
        const e = {};
        const ve = validateEmailCadastro(form.email); if (!ve.valid) e.email = ve.error;
        if (form.confirmEmail.trim().toLowerCase() !== form.email.trim().toLowerCase()) e.confirmEmail = 'Os emails não coincidem.';
        const vd = validateDateOfBirth(form.dataNascimento); if (!vd.valid) e.dataNascimento = vd.error;
        if (!form.password || form.password.length < 6) e.password = 'Mínimo de 6 caracteres.';
        if (!form.aceitouTermos) e.aceitouTermos = 'É preciso aceitar os termos.';
        setErros(e);
        return Object.keys(e).length === 0;
    };

    const irPasso3 = async () => {
        if (!validarPasso2() || verificando) return;
        setVerificando(true);
        try {
            const res = await verifyEmailExistApi({ userEmail: form.email });
            if (String(res?.data?.message || '').toLowerCase().includes('já cadastrado')) {
                notify('Este email já tem conta. Faça login.');
                navigate('/login', { state: { prefilledEmail: form.email } });
                return;
            }
            setEtapaCompra('carteira');
            setPasso(3);
        } catch {
            setEtapaCompra('carteira');
            setPasso(3);
        } finally {
            setVerificando(false);
        }
    };

    const validarPasso3 = () => {
        if (!produtoSel) { notify('Escolha um produto.'); return false; }
        if (!dados.instituicao.trim()) { notify('Informe a instituição de ensino.'); return false; }
        if (!dados.curso.trim()) { notify('Informe o curso.'); return false; }
        if (!dados.escolaridade) { notify('Selecione o nível de ensino.'); return false; }
        if (!dados.foto) { notify('Envie a foto da carteirinha.'); return false; }
        if (precisaEndereco && !frete) { notify('Calcule o frete, escolha a modalidade e confirme o endereço da carteirinha física.'); return false; }
        return true;
    };

    // Cria a conta (retorna token) e, já logado, gera o Pix com os dados da carteirinha.
    const criarContaEPagar = async () => {
        if (!validarPasso3() || enviando) return;
        setEnviando(true);
        let logou = false;
        try {
            const reg = await registerUserApi({
                nomeCompleto: form.nomeCompleto,
                email: form.email,
                cpf: form.cpf,
                phone: form.telefone,
                password: form.password,
                dataNascimento: form.dataNascimento,
            });
            const token = reg?.data?.token;
            if (!token) { notify('Não foi possível criar a conta. Tente novamente.'); return; }
            setToken(token);
            if (reg.data.user) setUser(reg.data.user);
            logou = true;

            const pixRes = await createPixPaymentApi({
                productId: produtoSel.id,
                ...(precisaEndereco ? { cepDestino: frete?.cepDestino, codigoServico: frete?.codigoServico } : {}),
                dadosCadastro: {
                    instituicao: dados.instituicao.trim(),
                    curso: dados.curso.trim(),
                    escolaridade: Number(dados.escolaridade),
                    foto: dados.foto,
                    ...(matricularFunemg ? { matricular_funemg: true, curso_ead_id: cursoEadId } : {}),
                    ...(dados.comprovante ? { comprovante: dados.comprovante } : {}),
                    ...(precisaEndereco ? { endereco: frete?.endereco } : {}),
                },
            });
            setPix(pixRes.data);
            setFase3('pix');
        } catch (err) {
            const msg = err?.response?.data?.error;
            if (logou) {
                // Conta criada, mas o Pix falhou: já está logado, manda pro portal.
                notify(msg || 'Conta criada! Não deu pra gerar o Pix agora — finalize em “Produtos”.');
                navigate('/');
                return;
            }
            if (msg && /(cadastrad|já tem conta)/i.test(msg)) {
                notify(msg);
                navigate('/login', { state: { prefilledEmail: form.email } });
                return;
            }
            notify(msg || 'Não foi possível concluir o cadastro.');
        } finally {
            setEnviando(false);
        }
    };

    // Passo 3 válido (silencioso) — usado pra habilitar/desabilitar o cartão.
    const passo3Valido = !!produtoSel && !!dados.instituicao.trim() && !!dados.curso.trim()
        && !!dados.escolaridade && !!dados.foto
        && (!precisaEndereco || !!frete);

    // Jornada COMPLETA do cadastro — uma barra de progresso única e coerente em
    // todos os passos (em vez de dois steppers com numerações diferentes). O
    // "Endereço de entrega" só entra quando a carteira escolhida é física.
    const jornada = [
        { key: 'dados', label: 'Seus dados' },
        { key: 'acesso', label: 'Acesso' },
        { key: 'carteira', label: 'Escolha da carteira' },
        { key: 'dadosCarteira', label: 'Dados da carteira' },
        ...(precisaEndereco ? [{ key: 'endereco', label: 'Entrega e frete' }] : []),
        { key: 'pagamento', label: 'Pagamento' },
    ];
    const jornadaKey = passo === 1 ? 'dados'
        : passo === 2 ? 'acesso'
        : fase3 !== 'form' ? 'pagamento'
        : ({ carteira: 'carteira', dados: 'dadosCarteira', endereco: 'endereco', pagamento: 'pagamento' })[etapaCompra];
    const jornadaIdx = Math.max(0, jornada.findIndex((e) => e.key === jornadaKey));

    // Avança pra próxima sub-etapa validando só o que a etapa atual pede.
    const avancarCompra = () => {
        if (etapaCompra === 'carteira') {
            if (!produtoSel) return notify('Escolha uma carteira.');
            return setEtapaCompra('dados');
        }
        if (etapaCompra === 'dados') {
            if (!dados.instituicao.trim()) return notify('Informe a instituição de ensino.');
            if (!dados.curso.trim()) return notify('Informe o curso.');
            if (!dados.escolaridade) return notify('Selecione o nível de ensino.');
            if (!dados.foto) return notify('Envie a foto da carteirinha.');
            return setEtapaCompra(precisaEndereco ? 'endereco' : 'pagamento');
        }
        if (etapaCompra === 'endereco') {
            if (!frete) return notify('Calcule o frete, escolha a modalidade e confirme o endereço.');
            return setEtapaCompra('pagamento');
        }
    };
    // Volta uma sub-etapa (ou pro passo 2 quando está na primeira).
    const voltarCompra = () => {
        if (etapaCompra === 'carteira') return setPasso(2);
        if (etapaCompra === 'dados') return setEtapaCompra('carteira');
        if (etapaCompra === 'endereco') return setEtapaCompra('dados');
        if (etapaCompra === 'pagamento') return setEtapaCompra(precisaEndereco ? 'endereco' : 'dados');
    };

    // Cartão: cria a conta e cobra na hora (aprovação síncrona).
    const criarContaEPagarCartao = async (cardToken, installments, billing) => {
        if (!validarPasso3() || enviando) return;
        setEnviando(true);
        let logou = false;
        try {
            const reg = await registerUserApi({
                nomeCompleto: form.nomeCompleto,
                email: form.email,
                cpf: form.cpf,
                phone: form.telefone,
                password: form.password,
                dataNascimento: form.dataNascimento,
            });
            const token = reg?.data?.token;
            if (!token) { notify('Não foi possível criar a conta. Tente novamente.'); return; }
            setToken(token);
            if (reg.data.user) setUser(reg.data.user);
            logou = true;

            const res = await createCardPaymentApi({
                productId: produtoSel.id,
                cardToken,
                installments,
                billing,
                ...(precisaEndereco ? { cepDestino: frete?.cepDestino, codigoServico: frete?.codigoServico } : {}),
                dadosCadastro: {
                    instituicao: dados.instituicao.trim(),
                    curso: dados.curso.trim(),
                    escolaridade: Number(dados.escolaridade),
                    foto: dados.foto,
                    ...(matricularFunemg ? { matricular_funemg: true, curso_ead_id: cursoEadId } : {}),
                    ...(dados.comprovante ? { comprovante: dados.comprovante } : {}),
                    ...(precisaEndereco ? { endereco: frete?.endereco } : {}),
                },
            });
            if (res.data?.approved || res.data?.status === 'approved') {
                setFase3('done');
            } else {
                notify('Cartão não aprovado. Sua conta foi criada — tente novamente em “Produtos”.');
                navigate('/');
            }
        } catch (err) {
            const msg = err?.response?.data?.error;
            if (logou) {
                notify(msg || 'Conta criada! Não deu pra cobrar o cartão agora — finalize em “Produtos”.');
                navigate('/');
                return;
            }
            if (msg && /(cadastrad|já tem conta)/i.test(msg)) {
                notify(msg);
                navigate('/login', { state: { prefilledEmail: form.email } });
                return;
            }
            notify(msg || 'Não foi possível concluir o cadastro.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <AuthShell semLateral>
            <div className={`ueb-auth-card ueb-reg ${passo === 3 && fase3 === 'form' ? 'ueb-reg--compra' : ''}`}>
                {/* Barra de progresso única da jornada — some só na tela final de
                    "pagamento aprovado". */}
                {!(passo === 3 && fase3 === 'done') && (
                    <div className="ueb-jornada">
                        <div className="ueb-jornada-cap">
                            <span className="ueb-jornada-atual">{jornada[jornadaIdx]?.label}</span>
                            <span className="ueb-jornada-cont">Etapa {jornadaIdx + 1} de {jornada.length}</span>
                        </div>
                        <div className="ueb-jornada-barra">
                            {jornada.map((et, i) => (
                                <span key={et.key} className={`ueb-jornada-seg ${i <= jornadaIdx ? 'is-on' : ''}`} />
                            ))}
                        </div>
                    </div>
                )}

                {passo === 1 && (
                    <>
                        <h1>Criar conta</h1>
                        <p className="ueb-auth-sub">Vamos começar com seus dados.</p>

                        <TextField id="nomeCompleto" label="Nome completo" placeholder="Seu nome e sobrenome"
                            value={form.nomeCompleto} onChange={set('nomeCompleto')} error={erros.nomeCompleto} />
                        <TextField id="cpf" label="CPF" inputMode="numeric" placeholder="000.000.000-00"
                            value={form.cpf} onChange={(e) => setForm((p) => ({ ...p, cpf: formatCPF(e.target.value) }))} error={erros.cpf} />
                        <TextField id="telefone" label="Telefone / WhatsApp" inputMode="tel" placeholder="(31) 99999-9999"
                            value={form.telefone} onChange={(e) => setForm((p) => ({ ...p, telefone: formatPhone(e.target.value) }))} error={erros.telefone} />

                        <button className="pbtn pbtn--primary pbtn--block pbtn--lg" onClick={irPasso2} disabled={verificando}>
                            {verificando ? <span className="pspin" /> : <>Continuar <ArrowRight size={18} /></>}
                        </button>
                        <p className="ueb-auth-foot">Já tem conta? <Link className="plink" to="/login">Entrar</Link></p>
                    </>
                )}

                {passo === 2 && (
                    <>
                        <h1>Dados de acesso</h1>
                        <p className="ueb-auth-sub">Com esse email e senha você vai entrar no portal.</p>

                        <TextField id="email" label="Email" type="email" placeholder="voce@email.com"
                            value={form.email} onChange={set('email')} error={erros.email} />
                        <TextField id="confirmEmail" label="Confirmar email" type="email" placeholder="repita o email"
                            value={form.confirmEmail} onChange={set('confirmEmail')} error={erros.confirmEmail} />
                        <TextField id="dataNascimento" label="Data de nascimento" type="date"
                            value={form.dataNascimento} onChange={set('dataNascimento')} error={erros.dataNascimento} />
                        <PasswordField id="password" label="Senha" autoComplete="new-password" placeholder="Mínimo de 6 caracteres"
                            value={form.password} onChange={set('password')} error={erros.password} />

                        <Field error={erros.aceitouTermos}>
                            <label className="ueb-check">
                                <input type="checkbox" checked={form.aceitouTermos}
                                    onChange={(e) => setForm((p) => ({ ...p, aceitouTermos: e.target.checked }))} />
                                <span>Li e aceito os <a className="plink" href="/termos-de-uso" target="_blank" rel="noreferrer">Termos de uso</a> e a <a className="plink" href="/politica-de-privacidade" target="_blank" rel="noreferrer">Política de privacidade</a>.</span>
                            </label>
                        </Field>

                        <div className="ueb-reg-actions">
                            <button className="pbtn pbtn--ghost" onClick={() => setPasso(1)}><ArrowLeft size={18} /> Voltar</button>
                            <button className="pbtn pbtn--primary" onClick={irPasso3} disabled={verificando} style={{ flex: 1 }}>
                                {verificando ? <span className="pspin" /> : <>Continuar <ArrowRight size={18} /></>}
                            </button>
                        </div>
                    </>
                )}

                {passo === 3 && fase3 === 'form' && (
                    produtos === null ? (
                        <div className="pcard" style={{ padding: 32, textAlign: 'center' }}><span className="pspin" style={{ color: 'var(--p-accent)' }} /></div>
                    ) : produtos.length === 0 ? (
                        <div className="palert palert--warning">
                            <ShoppingBag size={18} />
                            <span>Nenhum produto disponível para venda no momento. Tente novamente mais tarde.</span>
                        </div>
                    ) : (
                        <>
                            {/* Sub-etapa 1: escolha da carteira */}
                            {etapaCompra === 'carteira' && (
                                <>
                                    <h1>Escolha sua carteira</h1>
                                    <p className="ueb-auth-sub">Selecione o tipo de carteirinha que você quer emitir.</p>
                                    <div className="ueb-reg-produtos">
                                        {produtos.map((p) => {
                                            const Icon = iconeProduto(p);
                                            const sel = produtoSel?.id === p.id;
                                            return (
                                                <button type="button" key={p.id}
                                                    className={`ueb-reg-produto ${sel ? 'is-sel' : ''}`}
                                                    onClick={() => setProdutoSel(p)}>
                                                    <span className="ueb-reg-produto-ico"><Icon size={20} /></span>
                                                    <span className="ueb-reg-produto-info">
                                                        <strong>{p.title}</strong>
                                                        <small className="pfaint">{p.description}</small>
                                                    </span>
                                                    <span className="ueb-reg-produto-preco">{moeda(p.price)}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="ueb-reg-nav">
                                        <button className="pbtn pbtn--ghost" onClick={() => setPasso(2)}><ArrowLeft size={18} /> Voltar</button>
                                        <button className="pbtn pbtn--primary" onClick={avancarCompra} disabled={!produtoSel}>Continuar <ArrowRight size={18} /></button>
                                    </div>
                                </>
                            )}

                            {/* Sub-etapa 2: dados da carteirinha */}
                            {etapaCompra === 'dados' && (
                                <>
                                    <h1>Dados da carteirinha</h1>
                                    <p className="ueb-auth-sub">Esses dados vão impressos na sua carteira. Confira antes de continuar.</p>
                                    {produtoSel && (
                                        <div className="ueb-reg-resumo-bar">
                                            <span className="ueb-reg-resumo-bar-txt">{produtoSel.title}</span>
                                            <strong>{moeda(produtoSel.price)}</strong>
                                        </div>
                                    )}
                                    <div className="ueb-reg-dados">
                                        <label className="ueb-reg-switch">
                                            <input type="checkbox" checked={matricularFunemg} onChange={(e) => toggleFunemg(e.target.checked)} />
                                            <span className="ueb-reg-switch-track"><span className="ueb-reg-switch-thumb" /></span>
                                            <span className="ueb-reg-switch-label">Matricular na FUNEMG</span>
                                        </label>

                                        {matricularFunemg ? (
                                            <>
                                                <div className="ueb-reg-funemg-aviso">
                                                    <CheckCircle2 size={18} />
                                                    <span>Cadastrar na <strong>UEB</strong> dá direito a um <strong>cadastro gratuito</strong> em um curso da FUNEMG. A matrícula é liberada <strong>após o pagamento</strong></span>
                                                </div>
                                                <div className="ueb-reg-grid2">
                                                    <TextField id="instituicao" label="Instituição de ensino"
                                                        value={FUNEMG_INSTITUICAO} readOnly disabled />
                                                    <Field label="Curso" htmlFor="curso">
                                                        <select id="curso" className="pselect" value={cursoEadId}
                                                            onChange={(e) => selecionarCursoFunemg(e.target.value)}
                                                            disabled={!cursosFunemg.length}>
                                                            {!cursosFunemg.length && <option value="">Carregando cursos…</option>}
                                                            {cursosFunemg.map((c) => (
                                                                <option key={c.id} value={c.id}>{nomeCurso(c)}</option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                </div>
                                                <Field label="Nível de ensino" htmlFor="escolaridade">
                                                    <select id="escolaridade" className="pselect" value={TECNICO_ID} disabled>
                                                        <option value={TECNICO_ID}>Técnico</option>
                                                    </select>
                                                </Field>

                                                {cursoFunemgSel && (
                                                    <div className="ueb-reg-curso-card">
                                                        {cursoFunemgSel.capa_url && (
                                                            <img className="ueb-reg-curso-capa" src={cursoFunemgSel.capa_url}
                                                                alt={nomeCurso(cursoFunemgSel)} loading="lazy" />
                                                        )}
                                                        {cursoFunemgSel.descricao && (
                                                            <p className="ueb-reg-curso-desc">{cursoFunemgSel.descricao}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <div className="ueb-reg-grid2">
                                                    <TextField id="instituicao" label="Instituição de ensino" placeholder="Ex.: UEB"
                                                        value={dados.instituicao} onChange={setD('instituicao')} />
                                                    <CursoComboBox id="curso" label="Curso"
                                                        value={dados.curso} onChange={(v) => setDados((p) => ({ ...p, curso: v }))} />
                                                </div>
                                                <Field label="Nível de ensino" htmlFor="escolaridade">
                                                    <select id="escolaridade" className="pselect" value={dados.escolaridade} onChange={setD('escolaridade')}>
                                                        <option value="">Selecione…</option>
                                                        {ESCOLARIDADES.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
                                                    </select>
                                                </Field>
                                            </>
                                        )}
                                        <FotoCropUpload
                                            value={dados.foto}
                                            onChange={(v) => setDados((p) => ({ ...p, foto: v }))}
                                            onErro={notify}
                                        />
                                        {/* Matrícula na FUNEMG não pede comprovante (a matrícula no curso é a prova). */}
                                        {!matricularFunemg && (
                                            <ComprovanteUpload
                                                valor={dados.comprovante}
                                                onChange={(v) => setDados((p) => ({ ...p, comprovante: v }))}
                                                onErro={notify}
                                            />
                                        )}
                                    </div>
                                    <div className="ueb-reg-nav">
                                        <button className="pbtn pbtn--ghost" onClick={voltarCompra}><ArrowLeft size={18} /> Voltar</button>
                                        <button className="pbtn pbtn--primary" onClick={avancarCompra}>Continuar <ArrowRight size={18} /></button>
                                    </div>
                                </>
                            )}

                            {/* Sub-etapa 3: entrega + frete (só física) */}
                            {etapaCompra === 'endereco' && (
                                <>
                                    <h1>Entrega e frete</h1>
                                    <p className="ueb-auth-sub">Informe o endereço e escolha a forma de envio. O frete é obrigatório na carteirinha física.</p>
                                    {produtoSel && (
                                        <div className="ueb-reg-resumo-bar">
                                            <span className="ueb-reg-resumo-bar-txt">{produtoSel.title}</span>
                                            <strong>{moeda(produtoSel.price)}</strong>
                                        </div>
                                    )}
                                    <FreteCalculator produtoValor={produtoSel?.price || 0} onConfirmar={setFrete} />
                                    {frete && (
                                        <div className="palert palert--success" style={{ marginTop: 14 }}>
                                            <CheckCircle2 size={16} /> Entrega por {frete.modalidadeNome} confirmada — frete {moeda(frete.freteValor)}.
                                        </div>
                                    )}
                                    <div className="ueb-reg-nav">
                                        <button className="pbtn pbtn--ghost" onClick={voltarCompra}><ArrowLeft size={18} /> Voltar</button>
                                        <button className="pbtn pbtn--primary" onClick={avancarCompra} disabled={!frete}>Continuar <ArrowRight size={18} /></button>
                                    </div>
                                </>
                            )}

                            {/* Sub-etapa 4: pagamento */}
                            {etapaCompra === 'pagamento' && (
                                <>
                                    <h1>Pagamento</h1>
                                    <p className="ueb-auth-sub">Revise e finalize. A carteira é liberada após a confirmação.</p>
                                    <div className="ueb-reg-resumo-card">
                                        <div className="ueb-reg-pay-linha">
                                            <span>{produtoSel ? produtoSel.title : '—'}</span>
                                            <strong>{produtoSel ? moeda(produtoSel.price) : '—'}</strong>
                                        </div>
                                        {precisaEndereco && frete && (
                                            <div className="ueb-reg-pay-linha">
                                                <span>Frete ({frete.modalidadeNome})</span>
                                                <strong>{moeda(frete.freteValor)}</strong>
                                            </div>
                                        )}
                                        <div className="ueb-reg-pay-total">
                                            <span>Total</span>
                                            <strong>{produtoSel ? moeda(totalCompra) : '—'}</strong>
                                        </div>
                                    </div>

                                    {cartaoConfig.ativo && (
                                        <div className="ueb-reg-metodo">
                                            <button type="button" className={`pbtn ${metodoPag === 'pix' ? 'pbtn--primary' : 'pbtn--ghost'}`}
                                                onClick={() => setMetodoPag('pix')}><QrCode size={16} /> Pix</button>
                                            <button type="button" className={`pbtn ${metodoPag === 'cartao' ? 'pbtn--primary' : 'pbtn--ghost'}`}
                                                onClick={() => setMetodoPag('cartao')}><CartaoIco size={16} /> Cartão</button>
                                        </div>
                                    )}

                                    {cartaoConfig.ativo && metodoPag === 'cartao' ? (
                                        <CardForm
                                            publicKey={cartaoConfig.publicKey}
                                            botaoLabel={`Criar conta e pagar ${moeda(totalCompra)}`}
                                            processando={enviando}
                                            disabled={!passo3Valido}
                                            enderecoPadrao={precisaEndereco ? frete?.endereco : null}
                                            onPagar={criarContaEPagarCartao}
                                        />
                                    ) : (
                                        <button className="pbtn pbtn--primary pbtn--block pbtn--lg" onClick={criarContaEPagar} disabled={enviando}>
                                            {enviando ? <span className="pspin" /> : `Criar conta e pagar ${moeda(totalCompra)}`}
                                        </button>
                                    )}

                                    <div className="ueb-reg-nav ueb-reg-nav--voltar">
                                        <button className="pbtn pbtn--ghost" onClick={voltarCompra}><ArrowLeft size={18} /> Voltar</button>
                                    </div>
                                </>
                            )}
                        </>
                    )
                )}

                {passo === 3 && fase3 === 'pix' && pix && (
                    <>
                        <h1>Pague com Pix</h1>
                        <p className="ueb-auth-sub">Sua conta já foi criada. Conclua o pagamento para emitir a carteira.</p>
                        <PixWaiting pix={pix} onAprovado={() => setFase3('done')} onCancelar={() => navigate('/')} />
                    </>
                )}

                {passo === 3 && fase3 === 'done' && (
                    <div className="ueb-empty" style={{ textAlign: 'center' }}>
                        <span className="ueb-empty-ico" style={{ background: 'var(--p-accent-weak)', color: 'var(--p-accent)' }}><CheckCircle2 size={30} /></span>
                        <strong>Pagamento aprovado!</strong>
                        <p>Sua carteira entrou na fila de aprovação. Assim que for liberada, ela aparece em “Minha carteirinha”.</p>
                        <button className="pbtn pbtn--primary" style={{ marginTop: 10 }} onClick={() => navigate('/')}>Ir para o portal</button>
                    </div>
                )}
            </div>
        </AuthShell>
    );
}
